import React, { useState } from 'react';
import {
  ArrowLeft,
  Building2,
  Database,
  Calendar,
  RefreshCw,
  AlertCircle,
  Settings,
  Plus,
  KeyRound,
  Columns3,
  Tag,
  Brain,
  Trash2,
  ExternalLink,
  FolderTree,
  HeartPulse,
  SlidersHorizontal,
} from 'lucide-react';
import { useClient } from '../../contexts/ClientContext';
import EditClientModal from './EditClientModal';
import ColumnConfigModal from './ColumnConfigModal';
import ImportTomasModal from './ImportTomasModal';
import ClientUsersSection from './ClientUsersSection';
import TagsProcessingModal from './TagsProcessingModal';
import TomasHistoryList from './TomasHistoryList';
import CatalogadorPanel from './CatalogadorPanel';
import DataHealthPanel from './DataHealthPanel';
import FiltersConfigSection from './FiltersConfigSection';
import Categorias from '../catalogado/Categorias';
import TaxonomyEditor from './TaxonomyEditor';
import ConfigSectionShell from '../common/ConfigSectionShell';
import api from '../../utils/api';
import { buildClientSelection } from '../../utils/clientSelection';

export interface AdminClient {
  uid: string;
  name: string;
  display_name?: string;
  database?: any;
  logo?: string;
  logoHeight?: number;
  welcomeVideo?: string;
  active?: boolean;
  client_type?: string;
  tableCount?: number;
  lastTomaDate?: string | null;
  last_sync_at?: string;
  catalogadoPrompt?: string;
  enable_tags?: boolean;
  enable_sku?: boolean;
}

interface Props {
  client: AdminClient;
  onBack: () => void;
  onClientPatch?: (uid: string, patch: Record<string, any>) => void;
}

type Section =
  | 'general'
  | 'tomasDatos'
  | 'users'
  | 'categoryTree'
  | 'columns'
  | 'tags'
  | 'catalogador'
  | 'dataHealth'
  | 'filtros'
  | 'deleteTomas'
  | 'deleteClient';

interface MenuItem {
  id: Section;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  implemented: boolean;
  danger?: boolean;
  deprecated?: boolean;
}

// Orden de las secciones del config. Reordenar/ocultar acá cambia el sidebar Y el
// título de cada sección (el ícono+label DEBE coincidir con el título de la sección —
// ver ConfigSectionShell y docs/design-system/COMPONENT_CATALOG.md).
// NOTA: "Borrar Tomas" y "Borrar Cliente" se sacaron del menú (2026-08-07). El handler
// handleDeleteAllTomas + la ruta backend delete-all-tomas se conservan por si se
// reactivan; simplemente ya no hay entrada de menú que las exponga.
const MENU: MenuItem[] = [
  { id: 'general', label: 'General', icon: Settings, implemented: true },
  { id: 'tomasDatos', label: 'Tomas de Datos', icon: Database, implemented: true },
  { id: 'users', label: 'Usuarios', icon: KeyRound, implemented: true },
  { id: 'categoryTree', label: 'Categorías', icon: FolderTree, implemented: true },
  { id: 'columns', label: 'Columnas', icon: Columns3, implemented: true },
  { id: 'tags', label: 'Tags', icon: Tag, implemented: true },
  { id: 'catalogador', label: 'Catalogador IA', icon: Brain, implemented: true },
  { id: 'dataHealth', label: 'Data Health', icon: HeartPulse, implemented: true },
  { id: 'filtros', label: 'Filtros', icon: SlidersHorizontal, implemented: true },
];

const formatDateTime = (input: any, withTime: boolean) => {
  if (!input) return null;
  try {
    const d = new Date(input);
    if (isNaN(d.getTime())) return null;
    const opts: Intl.DateTimeFormatOptions = {
      day: '2-digit', month: '2-digit', year: 'numeric',
    };
    if (withTime) { opts.hour = '2-digit'; opts.minute = '2-digit'; opts.hour12 = false; }
    return new Intl.DateTimeFormat('es-AR', opts).format(d);
  } catch { return null; }
};

const ClientAdminPage: React.FC<Props> = ({ client, onBack, onClientPatch }) => {
  const [activeSection, setActiveSection] = useState<Section>('general');
  // CATEGORIAS: 2 tabs — Árbol (taxonomy editor v6.8) / Gestión (panel legacy).
  const [categoryTab, setCategoryTab] = useState<'arbol' | 'gestion'>('arbol');
  // TOMAS DE DATOS: 4 tabs — Origen / Visibilidad / Historial / Importar.
  const [tomasDatosTab, setTomasDatosTab] = useState<'origen' | 'visibilidad' | 'historial' | 'importar'>('origen');
  const { setSelectedClient, setIsInAdminMode } = useClient();

  const handleGoToClient = () => {
    setIsInAdminMode(false);
    // PT-#87: selección completa (enable_tags/enable_sku incluidos). Sin los flags, los
    // consumidores (`enable_tags !== false`) trataban a Adidas como cliente con tags.
    setSelectedClient(buildClientSelection(client.uid, client));
    // URL sin editClient para que no reabra Administrar Cliente al volver.
    window.history.replaceState(null, '', '/');
  };
  const lastTomaDate = client.lastTomaDate ? new Date(client.lastTomaDate) : null;
  const lastTomaHasTime = lastTomaDate
    ? (lastTomaDate.getUTCHours() !== 0 || lastTomaDate.getUTCMinutes() !== 0)
    : false;
  const lastSyncDate = client.last_sync_at ? new Date(client.last_sync_at) : null;

  const ageBucket: 'fresh' | 'stale' | 'old' | 'none' = (() => {
    if (!lastTomaDate) return 'none';
    const ageDays = (Date.now() - lastTomaDate.getTime()) / 86400000;
    if (ageDays > 90) return 'old';
    if (ageDays > 30) return 'stale';
    return 'fresh';
  })();
  const lastTomaColorClass = ({
    fresh: 'text-emerald-700',
    stale: 'text-amber-600',
    old: 'text-red-700',
    none: 'text-gray-800',
  } as const)[ageBucket];

  const handleSave = (patch?: Record<string, any>) => {
    if (patch && onClientPatch) onClientPatch(client.uid, patch);
  };

  const [deletingTomas, setDeletingTomas] = useState(false);
  const handleDeleteAllTomas = async () => {
    const userInput = prompt(
      `¿ABSOLUTAMENTE SEGURO que querés ELIMINAR TODAS LAS TOMAS de "${client.display_name || client.name}"?\n\n` +
      `Esta acción:\n- Elimina TODAS las tablas toma_*_${client.uid}\n- NO se puede deshacer\n\n` +
      `Escribí "ELIMINAR" para confirmar:`
    );
    if (userInput !== 'ELIMINAR') {
      alert('Operación cancelada.');
      return;
    }
    setDeletingTomas(true);
    try {
      const res = await api.delete(`/api/clients/${client.uid}/delete-all-tomas`, {
        headers: { 'x-client-id': client.uid, 'x-client-name': client.display_name || client.name },
      });
      if (res.data?.success) {
        alert(`✅ ${res.data.message}\nTablas eliminadas: ${res.data.deletedTables}`);
        if (onClientPatch) onClientPatch(client.uid, { tableCount: 0, lastTomaDate: null });
      } else {
        alert('Error: ' + (res.data?.error || 'desconocido'));
      }
    } catch (e: any) {
      alert('Error: ' + (e?.response?.data?.error || e?.message));
    } finally {
      setDeletingTomas(false);
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'general':
        return (
          <EditClientModal
            isOpen={true}
            inline={true}
            section="datosBasicos"
            client={client as any}
            onClose={() => { /* inline: no se cierra */ }}
            onSave={handleSave}
          />
        );
      case 'tomasDatos':
        return (
          <ConfigSectionShell
            icon={Database}
            title="Tomas de Datos"
            clientName={client.display_name || client.name}
            tabs={[
              {
                id: 'origen',
                label: 'Origen',
                icon: Database,
                description: 'Configuración de la base de datos de origen (conexión, credenciales, tabla).',
              },
              {
                id: 'visibilidad',
                label: 'Visibilidad',
                icon: Settings,
                description: 'Política de visibilidad de tomas nuevas y estados de toma visibles en la plataforma.',
              },
              {
                id: 'historial',
                label: 'Historial',
                icon: Calendar,
                description: 'Historial de tomas importadas/sincronizadas para este cliente.',
              },
              {
                id: 'importar',
                label: 'Importar',
                icon: Plus,
                description: 'Importar nuevas tomas de datos al cliente.',
              },
            ]}
            activeTab={tomasDatosTab}
            onTabChange={(id) => setTomasDatosTab(id as 'origen' | 'visibilidad' | 'historial' | 'importar')}
          >
            {tomasDatosTab === 'origen' && (
              <EditClientModal
                isOpen={true}
                inline={true}
                section="origen"
                client={client as any}
                onClose={() => { /* inline */ }}
                onSave={handleSave}
              />
            )}
            {tomasDatosTab === 'visibilidad' && (
              <EditClientModal
                isOpen={true}
                inline={true}
                section="visibilidad"
                client={client as any}
                onClose={() => { /* inline */ }}
                onSave={handleSave}
              />
            )}
            {tomasDatosTab === 'historial' && (
              <TomasHistoryList
                clientId={client.uid}
                clientName={client.display_name || client.name}
              />
            )}
            {tomasDatosTab === 'importar' && (
              <ImportTomasModal
                inline={true}
                client={client as any}
                onClose={() => { /* inline */ }}
                onImportComplete={() => { /* refresh manual si hace falta */ }}
              />
            )}
          </ConfigSectionShell>
        );
      case 'categoryTree':
        return (
          <ConfigSectionShell
            icon={FolderTree}
            title="Categorías"
            clientName={client.display_name || client.name}
            tabs={[
              {
                id: 'arbol',
                label: 'Árbol',
                icon: FolderTree,
                description: 'Árbol de categorías (taxonomía) — source of truth consumida por el catalogador, validador y filtros.',
              },
              {
                id: 'gestion',
                label: 'Gestión',
                icon: Settings,
                description: 'Panel legacy de gestión de categorías (atributos globales, reglas, clasificación).',
              },
            ]}
            activeTab={categoryTab}
            onTabChange={(id) => setCategoryTab(id as 'arbol' | 'gestion')}
          >
            {categoryTab === 'arbol' ? (
              <TaxonomyEditor clientId={client.uid} />
            ) : (
              <Categorias
                clientIdOverride={client.uid}
                clientNameOverride={client.display_name || client.name}
              />
            )}
          </ConfigSectionShell>
        );
      case 'columns':
        return (
          <ColumnConfigModal
            isOpen={true}
            inline={true}
            clientId={client.uid}
            clientName={client.display_name || client.name}
            onClose={() => { /* inline */ }}
          />
        );
      case 'users':
        return <ClientUsersSection client={client as any} />;
      case 'tags':
        return (
          <TagsProcessingModal
            inline={true}
            clientId={client.uid}
            clientName={client.display_name || client.name}
            onClose={() => { /* inline */ }}
            onCompleted={() => { /* refresh */ }}
          />
        );
      case 'deleteTomas':
        return (
          <div className="bg-white rounded-lg border border-red-200 p-6">
            <h3 className="text-base font-semibold text-red-800 mb-2 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Borrar todas las tomas
            </h3>
            <p className="text-sm text-gray-700 mb-3">
              Elimina <strong>todas las tablas toma_*</strong> de la base de datos del cliente.
              No afecta otros clientes. <strong>Acción irreversible.</strong>
            </p>
            <ul className="text-xs text-gray-500 list-disc ml-5 mb-4 space-y-0.5">
              <li>Las tablas <code className="text-gray-700">toma_*_{client.uid}</code> quedan dropeadas en MariaDB.</li>
              <li>El cliente queda con 0 tomas hasta que se reimporten o sincronicen nuevas.</li>
              <li>Los datos en Firestore <code className="text-gray-700">client_tomas/{client.uid}</code> NO se borran (se quedan vacíos al próximo sync).</li>
            </ul>
            <button
              onClick={handleDeleteAllTomas}
              disabled={deletingTomas}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded transition-colors disabled:opacity-50"
            >
              {deletingTomas ? '...' : 'Borrar todas las tomas'}
            </button>
          </div>
        );
      case 'catalogador':
        return (
          <CatalogadorPanel
            clientId={client.uid}
            clientName={client.display_name || client.name}
          />
        );
      case 'dataHealth':
        return (
          <DataHealthPanel
            clientId={client.uid}
            clientName={client.display_name || client.name}
          />
        );
      case 'filtros':
        return (
          <FiltersConfigSection
            clientId={client.uid}
            clientName={client.display_name || client.name}
          />
        );
      case 'deleteClient':
        return (
          <div className="bg-red-50 rounded-lg border border-red-200 p-8 text-center">
            <Trash2 className="w-12 h-12 text-red-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-red-800">Borrar Cliente</h3>
            <p className="text-sm text-red-600 mt-2">Pendiente de implementar.</p>
            <p className="text-xs text-red-500 mt-1">
              Acción destructiva: borrará todos los datos del cliente en Firestore + MariaDB.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
        {/* Header */}
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-3 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a System Admin
        </button>
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900 leading-tight">Administrar Cliente</h1>
        </div>

        {/* Summary card — full width, mismo estilo que el listado */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex items-center gap-4 py-4 pl-4 pr-5">
            {/* Logo + nombre */}
            <div className="flex items-center gap-3 w-[320px] flex-shrink-0">
              <div className="flex-shrink-0 w-36 h-14 flex items-center justify-center bg-gray-50 rounded border border-gray-200 px-2">
                {client.logo ? (
                  <img src={client.logo} alt={client.display_name || client.name} className="object-contain max-h-10 max-w-full" />
                ) : (
                  <Building2 className="w-7 h-7 text-gray-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-gray-900 truncate leading-tight">
                  {client.display_name || client.name}
                </h3>
                <p className="text-xs font-mono text-gray-400 truncate mt-0.5">{client.uid}</p>
              </div>
            </div>

            {/* Pastilla tomas */}
            <div className="hidden md:flex w-[130px] flex-shrink-0" style={{ marginLeft: '4em' }}>
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg w-full">
                <Database className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <span className="text-sm font-bold text-blue-900">
                  {client.tableCount ?? 0} tomas
                </span>
              </div>
            </div>

            {/* Fechas */}
            <div className="hidden md:flex flex-col gap-1 w-[290px] flex-shrink-0">
              <div className="flex items-center gap-2 text-xs">
                <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="text-gray-500 w-[80px] flex-shrink-0">Última toma:</span>
                <span className={`font-semibold tabular-nums ${lastTomaColorClass}`}>
                  {formatDateTime(lastTomaDate, lastTomaHasTime) || '—'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <RefreshCw className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="text-gray-500 w-[80px] flex-shrink-0">Último sync:</span>
                <span className="font-semibold text-gray-800 tabular-nums">
                  {formatDateTime(lastSyncDate, true) || 'nunca'}
                </span>
              </div>
            </div>

            <div className="flex-1" />

            {/* Status badge */}
            {client.active === false && (
              <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                Inactivo
              </span>
            )}
            {client.client_type === 'demo' && (
              <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                Demo
              </span>
            )}

            {/* Ir a cliente — mismo botón que en Gestión de Clientes */}
            <button
              onClick={handleGoToClient}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded transition-colors shadow-sm flex-shrink-0"
              title="Ir a cliente"
            >
              <ExternalLink className="w-4 h-4" />
              <span className="hidden lg:inline">Ir a cliente</span>
            </button>
          </div>
        </div>

        {/* Layout split: sidebar + content */}
        <div className="flex gap-6">
          {/* Sidebar */}
          <aside className="w-56 flex-shrink-0">
            <nav className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              {MENU.map((item, idx) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    className={`w-full text-left px-4 py-2.5 flex items-center gap-2.5 text-sm transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 border-l-2 border-blue-600 font-medium'
                        : 'text-gray-700 hover:bg-gray-50 border-l-2 border-transparent'
                    } ${idx > 0 ? 'border-t border-gray-100' : ''} ${item.danger && !isActive ? 'text-red-600' : ''}`}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${
                      isActive ? 'text-blue-600' : item.danger ? 'text-red-500' : 'text-gray-400'
                    }`} />
                    <span className="truncate">{item.label}</span>
                    {item.deprecated ? (
                      <span className="ml-auto text-[10px] text-amber-600 font-mono">deprecado</span>
                    ) : !item.implemented ? (
                      <span className="ml-auto text-[10px] text-gray-400 font-mono">soon</span>
                    ) : null}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Content panel */}
          <main className="flex-1 min-w-0">
            {renderContent()}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ClientAdminPage;
